import { showError } from '@components/app-error';
import { showDialog } from '@components/dialog';
import { FormGroup } from '@components/async-form';
import { BtnCopy, BtnPrimary } from '@components/buttons';
import { Case } from '@components/conditional';
import { useRouteParams } from '@components/router';
import { useCurrentTenant } from '@components/router/session-context';
import { Toggle } from '@components/toggle';
import { rpx } from 'client/lib/rpx-client';
import { useAsyncEffect } from 'client/utils/use-async-effect';
import { JSX, ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { FullCourse, Meeting } from 'server/types';
import { handleZoomIntegrationError } from './helpers';
import { RuzukuCallDetails } from './ruzuku-call-details';
import { MiraseeBadge } from '@components/mirasee-badge';
import {
  IcoExternalLink,
  IcoPresentation,
  IcoVideoCamera,
  IcoZoom,
  LocationIcon,
} from '@components/icons';

export type MeetingTypeData = Pick<
  Meeting,
  | 'scheduledAt'
  | 'type'
  | 'slidesFile'
  | 'recordMeeting'
  | 'allowRecordingDownloads'
  | 'externalJoinUrl'
  | 'zoomMeetingId'
  | 'zoomPassword'
  | 'zoomHasWaitingRoom'
  | 'inpersonLocation'
  | 'requiresRsvp'
  | 'rsvpEnabled'
> & {
  id?: UUID;
};

interface RadioProps extends JSX.InputHTMLAttributes<HTMLInputElement> {
  title: string;
  desc: ComponentChildren;
  details?: ComponentChildren;
  icon?: ComponentChildren;
}

interface Props {
  course: Pick<FullCourse, 'id' | 'level' | 'defaultMeetingJoinUrl'>;
  canEditDetails: boolean;
  onChange: (
    subset: Partial<MeetingTypeData> & { sendRsvpEmail?: boolean },
    isLocalUpdate?: boolean,
  ) => void;
  onSlideDelete: () => void;
}

const store = rpx.zoom;

function zoomJoinLink(meeting: Pick<Meeting, 'zoomMeetingId' | 'zoomPassword'>) {
  if (!meeting.zoomPassword || !meeting.zoomMeetingId) {
    return undefined;
  }
  return `https://zoom.us/j/${meeting.zoomMeetingId}?pwd=${meeting.zoomPassword}`;
}

function MeetingTypeRadio({ title, desc, details, icon, ...props }: RadioProps) {
  const cursor = !props.checked && !props.disabled ? 'cursor-pointer' : '';
  return (
    <div
      class={`my-2 p-4 pr-8 rounded-md border text-sm ${
        props.checked ? 'border-indigo-600 ring-1 ring-indigo-600 text-gray-700' : cursor
      } `}
    >
      <label class={`flex items-start ${cursor}`}>
        <input
          type="radio"
          class={`focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 mr-4 mt-1 ${
            props.disabled ? 'opacity-40 cursor-not-allowed' : ''
          }`}
          {...props}
        />

        <div class="flex flex-row items-start w-full">
          {!!icon && <div class="flex flex-col mr-4">{icon}</div>}
          <span class="flex flex-col gap-4">
            <span class={`font-bold ${props.disabled ? 'opacity-40' : ''}`}>{title}</span>
            <p class="text-gray-500 text-sm opacity-100">{desc}</p>
            {!!details && props.checked && <div>{details}</div>}
          </span>
        </div>
      </label>
    </div>
  );
}

function ZoomCallDetails({
  meeting,
  course,
  canEditDetails,
  onChange,
}: {
  meeting: MeetingTypeData;
  canEditDetails: boolean;
  course: Props['course'];
  onChange: Props['onChange'];
}) {
  const { courseId } = useRouteParams();
  const url = zoomJoinLink(meeting);
  const [isCreatingZoomMeeting, setIsCreatingZoomMeeting] = useState(false);

  async function createZoomMeeting() {
    if (!meeting.id) {
      return;
    }

    setIsCreatingZoomMeeting(true);
    try {
      const result = await rpx.meetings.createMissingZoomMeeting({
        meetingId: meeting.id,
        courseId,
      });
      onChange(
        {
          zoomMeetingId: result.id,
          zoomPassword: result.password,
        },
        true,
      );
    } catch (err) {
      const isHandled = await handleZoomIntegrationError({
        err,
        course,
        meetingId: meeting.id,
      });
      if (!isHandled) {
        showError(err);
      }
      setIsCreatingZoomMeeting(false);
    }
  }

  return (
    <div>
      {!!meeting.id && canEditDetails && (
        <div class="border-b pb-4">
          {!!url && (
            <div class="flex justify-between items-center grow bg-indigo-100 rounded-lg p-4">
              <span class="max-w-96 break-words">{url}</span>
              <BtnCopy value={url}>Copy student invitation link</BtnCopy>
            </div>
          )}
          {!url && meeting.scheduledAt && (
            <BtnPrimary isLoading={isCreatingZoomMeeting} onClick={createZoomMeeting}>
              Show invitation link
            </BtnPrimary>
          )}
        </div>
      )}
      <div class="pt-4 gap-3">
        <label
          class={`flex items-center gap-3 ${
            canEditDetails ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'
          }`}
        >
          <Toggle
            checked={meeting.recordMeeting}
            disabled={!canEditDetails}
            onClick={() =>
              onChange({
                recordMeeting: !meeting.recordMeeting,
              })
            }
          />
          <span>Record Event</span>
        </label>
        <p class="text-xs mt-2 mb-4">
          {canEditDetails
            ? 'Please note that you may start and pause the the session recording anytime after the event starts.'
            : 'This option is disabled because the meeting has started.'}
        </p>
        <label class="flex items-center gap-3 cursor-pointer pb-4">
          <Toggle
            checked={meeting.allowRecordingDownloads && (!canEditDetails || meeting.recordMeeting)}
            onClick={() =>
              onChange({
                allowRecordingDownloads: !meeting.allowRecordingDownloads,
              })
            }
          />
          <span>Allow students to download recordings</span>
        </label>
        <div class="pt-4 border-t">
          <label class="flex items-center gap-3 cursor-pointer">
            <Toggle
              checked={meeting.zoomHasWaitingRoom}
              onClick={() =>
                onChange({
                  zoomHasWaitingRoom: !meeting.zoomHasWaitingRoom,
                })
              }
            />
            <span>Enable Waiting Room</span>
          </label>
          {meeting.zoomMeetingId && (
            <p class="text-xs mt-2 mb-4">
              You can visit{' '}
              <a
                href={`https://zoom.us/meeting/${meeting.zoomMeetingId}/edit`}
                target="_blank"
                rel="noopener noreferrer"
              >
                the Zoom page of this meeting
              </a>{' '}
              to see all configuration options.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function MiraseeZoomCard({ url }: { url: string }) {
  return (
    <div class="my-2 rounded-lg border border-[#003da6]/20 bg-gradient-to-r from-[#003da6]/5 to-transparent p-5">
      <div class="flex items-center gap-3 mb-3">
        <IcoZoom class="h-5 w-auto" />
        <MiraseeBadge size="sm" />
      </div>
      <p class="text-sm text-gray-700 mb-3">
        This event uses the Mirasee Zoom room. Recording and transcript will be captured
        automatically.
      </p>
      <div class="flex items-center gap-2 bg-white rounded-md border border-gray-200 px-3 py-2 text-sm">
        <IcoExternalLink class="text-[#003da6] w-4 h-4 shrink-0" />
        <span class="text-gray-600 font-medium">{url}</span>
      </div>
    </div>
  );
}

export function MeetingTypes(
  props: Props & {
    meeting: MeetingTypeData;
    course: Pick<FullCourse, 'id' | 'level' | 'defaultMeetingJoinUrl'>;
    canEditDetails: boolean;
    hideDetails?: boolean;
  },
) {
  const { meeting, hideDetails, onChange } = props;
  const tenant = useCurrentTenant();
  const { terminology } = tenant;
  const [hasZoomAccount, setHasZoomAccount] = useState(false);

  useAsyncEffect(async () => {
    const result = await store.getZoomAccountForCourse({
      courseId: props.course.id,
    });
    setHasZoomAccount(result.hasAccount);
  }, []);

  if (props.course.defaultMeetingJoinUrl && meeting.type === 'external') {
    return <MiraseeZoomCard url={props.course.defaultMeetingJoinUrl} />;
  }

  return (
    <>
      {!tenant.isCore && (
        <>
          <MeetingTypeRadio
            title="Video Conference"
            desc="The host and participants may stream their audio/video in this option. Support up to 60 participants."
            icon={<IcoVideoCamera className="text-indigo-600 w-6 h-6" />}
            checked={meeting.type === 'videoconference'}
            details={!hideDetails && <RuzukuCallDetails {...props} />}
            onClick={() => onChange({ type: 'videoconference' })}
          />
          <MeetingTypeRadio
            title="Presentation"
            desc="Only the host streams audio/video in this option. Supports up to 250 participants."
            icon={<IcoPresentation className="text-green-600 w-6 h-6" />}
            checked={meeting.type === 'presentation'}
            details={!hideDetails && <RuzukuCallDetails {...props} />}
            onClick={() => onChange({ type: 'presentation' })}
          />
        </>
      )}
      <MeetingTypeRadio
        title="Zoom"
        disabled={!hasZoomAccount}
        icon={<IcoZoom className="w-6 h-6" />}
        desc={
          <span>
            <Case
              when={!hasZoomAccount}
              fallback={
                <>
                  Host your {terminology.meeting} on Zoom.
                  {props.course.level === 'facilitator' && (
                    <span class="ml-1">
                      This option uses the {terminology.guide}'s Zoom integration.
                    </span>
                  )}
                </>
              }
            >
              <Case
                when={props.course.level === 'guide'}
                fallback={`The ${terminology.guide} needs to integrate Zoom account first to host your ${terminology.meeting} on Zoom.`}
              >
                <a href="/account/integrations">Configure Zoom </a>
                if you want to host your {terminology.meeting} on Zoom.
              </Case>
            </Case>
          </span>
        }
        checked={meeting.type === 'zoom'}
        details={!hideDetails && <ZoomCallDetails {...props} />}
        onClick={() => onChange({ type: 'zoom' })}
      />
      <MeetingTypeRadio
        title="External"
        desc={`Host your ${terminology.meeting} on an external platform.`}
        icon={
          <div>
            <IcoExternalLink className="text-gray-600 w-6 h-6" />
          </div>
        }
        checked={meeting.type === 'external'}
        onClick={() => onChange({ type: 'external' })}
        details={
          !hideDetails && (
            <FormGroup prop="externalJoinUrl">
              <p class="text-gray-500 text-sm mb-2">
                Join URL is required for external {terminology.meetings}.
              </p>
              <input
                type="text"
                class="ruz-input w-1/2"
                placeholder={`Join ${terminology.meeting} URL`}
                value={meeting.externalJoinUrl}
                onBlur={(e: any) => onChange({ externalJoinUrl: e.target.value })}
              />
              <p class="text-gray-500 text-sm mt-4">
                You may also share any other call details in the description section.
              </p>
            </FormGroup>
          )
        }
      />

      <div>
        <MeetingTypeRadio
          title="In-Person"
          desc={`Host your ${terminology.meeting} in-person at a specific location.`}
          checked={meeting.type === 'inperson'}
          icon={
            <div>
              <LocationIcon />
            </div>
          }
          onClick={() => onChange({ type: 'inperson' })}
          details={
            <FormGroup prop="inpersonLocation">
              <p class="text-gray-500 text-sm mb-2">
                Indicate a location for this {terminology.meeting}.
              </p>
              <input
                type="text"
                class="ruz-input w-1/2"
                placeholder={`Location`}
                value={meeting.inpersonLocation}
                onBlur={(e: any) => onChange({ inpersonLocation: e.target.value })}
              />
              <p class="text-gray-500 text-sm mt-4">
                You may also share any other call details in the description section.
              </p>
            </FormGroup>
          }
        />
      </div>
    </>
  );
}

export function TypeTab({
  meeting,
  course,
  canEditDetails,
  onChange,
  onSlideDelete,
}: Props & {
  meeting: { id: UUID } & MeetingTypeData;
}) {
  const joinPageUrl = `/join/${meeting.id}`;

  return (
    <div class="py-8">
      <MeetingTypes
        meeting={meeting}
        course={course}
        canEditDetails={canEditDetails}
        onChange={onChange}
        onSlideDelete={onSlideDelete}
      />
      <div class="mt-8 pt-8 border-t border-gray-200">
        <div class="flex items-start gap-3 mb-4">
          <Toggle
            checked={meeting.requiresRsvp}
            onClick={() => onChange({ requiresRsvp: !meeting.requiresRsvp })}
          />
          <div>
            <p class="text-sm font-medium text-gray-700">Enable Bridge Page</p>
            <p class="text-sm text-gray-500">
              Attendees visit a branded page where they identify themselves before joining the call.
              This tracks attendance by name and email.
            </p>
          </div>
        </div>
        {meeting.requiresRsvp && (
          <div class="flex items-center gap-3 ml-12">
            <code class="flex-1 text-sm bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono truncate">
              {joinPageUrl}
            </code>
            <BtnCopy
              class="shrink-0"
              value={
                typeof window !== 'undefined'
                  ? `${window.location.origin}${joinPageUrl}`
                  : joinPageUrl
              }
            />
          </div>
        )}
      </div>
      <div class="mt-8 pt-8 border-t border-gray-200">
        <div class="flex items-start gap-3">
          <Toggle
            checked={meeting.rsvpEnabled}
            onClick={async () => {
              if (meeting.rsvpEnabled) {
                onChange({ rsvpEnabled: false });
                return;
              }
              if (meeting.scheduledAt) {
                const sendEmail = await showDialog({
                  mode: 'info',
                  title: 'Enable RSVP',
                  children:
                    'Would you like to send an invitation email to all enrolled students? You can also enable RSVP without sending an email.',
                  confirmButtonText: 'Enable & Send Email',
                  cancelButtonText: 'Enable Without Email',
                });
                onChange({ rsvpEnabled: true, sendRsvpEmail: sendEmail });
              } else {
                onChange({ rsvpEnabled: true });
              }
            }}
          />
          <div>
            <p class="text-sm font-medium text-gray-700">Enable RSVP</p>
            <p class="text-sm text-gray-500">
              Students can register their attendance in advance. You'll see a headcount on the
              Attendance tab.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
